import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";
import {AllergyElement} from "crqlar-react-common/dist/components/uiElements/AllergyElement";
import {DietElement} from "crqlar-react-common/dist/components/uiElements/DietElement";
import {InterestElement} from "crqlar-react-common/dist/components/uiElements/InterestElement";
import {MedicalElement} from "crqlar-react-common/dist/components/uiElements/MedicalElement";
import {
    Allergy,
    COMMON_PREFERENCE_TYPES,
    DietType,
    Interest,
    MedicalCondition,
} from "crqlar-react-common/dist/models/profile/preference.model";
import {labelizeUnderScoreText, useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {ElementChipList} from "../../common/uiElements/base/ElementChipList";

interface ChipElementProps {
    label: string;
    value?: string;
    aiGenerated?: boolean;
}

interface Category {
    title: string;
    Element: (props: ChipElementProps) => React.ReactElement | null;
    values: string[];
    labelKey: (value: string) => string;
    /** Shown as typed, like a custom or PMS value: not in the list, so the category icon. */
    customText: string;
}

const CATEGORIES: Category[] = [
    {
        title: "Allergies",
        Element: AllergyElement,
        values: Object.values(Allergy),
        labelKey: value => `${COMMON_PREFERENCE_TYPES.CRQLAR_FOOD_ALLERGY}_${value}`,
        customText: "Nüsse",
    },
    {
        title: "Diets",
        Element: DietElement,
        values: Object.values(DietType),
        labelKey: value => `${COMMON_PREFERENCE_TYPES.CRQLAR_FOOD_TYPE}_${value}`,
        customText: "vegan",
    },
    {
        title: "Medical",
        Element: MedicalElement,
        values: Object.values(MedicalCondition),
        labelKey: value => `Common health preference value ${value}`,
        customText: "Knie-OP",
    },
    {
        title: "Interests",
        Element: InterestElement,
        values: Object.values(Interest),
        labelKey: value => `${COMMON_PREFERENCE_TYPES.CRQLAR_INTERESTS}_${value}`,
        customText: "Kochkurs",
    },
];

const ChipIconsTestPage = () => {
    const {tFallback} = useLocalize();

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: 0,
                overflowY: "auto",
                bgcolor: "background.default",
                px: {xs: 2, md: 6},
                py: {xs: 3, md: 5},
            }}
        >
            <Stack gap={4} sx={{width: "100%", maxWidth: 1100}}>
                <Box>
                    <Typography variant="h4">Chip icons</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, maxWidth: 720}}>
                        Every list value with its icon, in the app's language. A custom or PMS text keeps the
                        category icon; an AI-found interest shows the sparkle.
                    </Typography>
                </Box>

                {CATEGORIES.map(({title, Element, values, labelKey, customText}) => (
                    <Stack key={title} gap={1.5}>
                        <Typography variant="h6">{title}</Typography>
                        <ElementChipList>
                            {values.map(value => (
                                <Element
                                    key={value}
                                    label={tFallback(labelKey(value), labelizeUnderScoreText(value))}
                                    value={value}
                                />
                            ))}
                        </ElementChipList>
                        <Typography variant="body2" color="text.secondary">Custom text</Typography>
                        <ElementChipList>
                            <Element label={customText} value={customText}/>
                            {Element === InterestElement && (
                                <Element label="Weinverkostung (AI)" value="Weinverkostung" aiGenerated/>
                            )}
                        </ElementChipList>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
};

export default ChipIconsTestPage;
